import { PaymentHandler, PayResult } from '@/store/models';
import { getHipayPayment, sendHipayTokenizedCard } from '@/store/api';
import { i18n } from '@/i18n';
import { success } from '@/commons/toast';
import store from '@/store';
import ui from '@/store/modules/ui';
import cart from '@/store/modules/cart';
import checkout from '@/store/modules/checkout';
import payment from '@/store/modules/payment';
import errorHandler from '@/commons/errorHandler';
import scriptLoad from '@/commons/scriptLoad';

export default class HiPay implements PaymentHandler {
    private hipayInstance: any;
    private cardInstance: any;
    private tokenizedPaymentResult: any;

    public init(params: any) {
        return new Promise((resolve, reject) => {
            if (params.use_hostedfields && params.mode !== 'paypal') {
                this.createHipayInstance(params).then((res) => {
                    resolve(res);
                });
            } else {
                getHipayPayment(params.mode, params.shouldTokenizeCard).then((res) => {
                    resolve({
                        handler: this,
                        iframesrc: res,
                    });
                }).catch((e) => {
                    if (params.tryrestorepayment && e.response.data && e.response.data.code === 87) {
                        payment.finalize({payData: {}, useStoredData: true });
                        // tslint:disable-next-line:max-line-length
                        const mess = i18n.t('The order is already been paid, please wait while order is placing').toString();
                        success(mess);
                        reject(mess);
                    } else {
                        reject(e);
                    }
                });
            }
        });
    }

    public pay(amount: number): Promise<PayResult> {
        const methodCode = checkout.selectedPaymentMethod.code;
        const isPaypal = methodCode === 'hipay_paypal';
        const cardTokenization = payment.additionalData.hipay_tokenization;
        const hipayConfig = store.state.options.payment.hipayConfig;

        return new Promise((resolve, reject) => {
            if (methodCode === 'hipay' && cardTokenization.tokenId) { // saved card
                cart.flagLoading({key: 'processingpayment', value: true});
                getHipayPayment('tokenizedcard', false, cardTokenization.tokenId).then((forwardUrl: string) => {
                    this.handleCreatePaymentResponse(forwardUrl);
                }).catch((e) => {
                    reject(e);
                });
            } else if (methodCode === 'hipay' && hipayConfig.use_hostedfields) { // hosted fields
                sendHipayTokenizedCard(this.tokenizedPaymentResult).then((forwardUrl: string) => {
                    this.handleCreatePaymentResponse(forwardUrl);
                }).catch((e) => {
                    reject(e.response.data.message);
                });
            } else { // paypal e hipay iframe
                payment.unregisterMethod(methodCode).then(() => {
                    if (isPaypal) {
                        hipayConfig.mode = 'paypal';
                    } else {
                        ui.openModal('hipay');
                        cart.flagLoading({key: 'hipay', value: true});
                    }
                    hipayConfig.shouldTokenizeCard = cardTokenization.tokenize_card;
                    payment.registerMethod({ methodCode, params: hipayConfig }).then(() => {
                        const methodData = payment.dataByMethod(methodCode);
                        if (!isPaypal) {
                            this.createIframe(methodData.iframesrc);
                            cart.flagLoading({key: 'hipay', value: false});
                        } else { // hipay paypal
                            cart.flagLoading({key: 'processingpayment', value: true});
                            window.location.href = methodData.iframesrc;
                        }
                        resolve({needsFinalization: false});
                    }).catch((e) => {
                        reject(errorHandler(e, false, false).message);
                    });
                });
            }
        });
    }

    public teardown() {
        delete this.tokenizedPaymentResult;
        if (this.cardInstance) {
            this.cardInstance.destroy();
            delete this.cardInstance;
        }
        const $cont = document.getElementById('hipay-payment-form');
        if ($cont && $cont.children && $cont.children.length) {
            $cont.removeChild($cont.children[0]);
        }
        return new Promise((resolve, reject) => {
            resolve(true);
        });
    }

    public validate() {
        const methodCode = checkout.selectedPaymentMethod.code;
        const hipayConfig = store.state.options.payment.hipayConfig;
        const cardTokenization = payment.additionalData.hipay_tokenization;

        return new Promise((resolve, reject) => {
            let errors: any = [];
            if (methodCode === 'hipay' && hipayConfig.use_hostedfields && cardTokenization.tokenId === 0) {
                this.cardInstance.setMultiUse(cardTokenization.tokenize_card);
                this.cardInstance.getPaymentData().then(
                    (res: any) => {
                        cart.flagLoading({key: 'processingpayment', value: true});
                        this.tokenizedPaymentResult = res;
                        resolve(errors);
                    },
                    (errs: any) => {
                        errors = errs.map((e: any) => e.error);
                        resolve(errors);
                    },
                );
            } else {
                resolve(errors);
            }
        });
    }

    private createIframe(src: string) {
        const $cont = document.getElementById('hipay-payment-form') as HTMLElement;
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.width = '100%';
        iframe.height = '500px';
        $cont.appendChild(iframe);
    }

    private handleCreatePaymentResponse(forwardUrl: string) {
        window.location.href = forwardUrl;
        // if (forwardUrl.indexOf(store.state.options.base_url) === -1) {
        //     this.createIframe(forwardUrl);
        //     ui.openModal('hipay');
        // } else { // is checkout/onepage/success
        //     window.location.href = forwardUrl;
        // }
    }

    private createHipayInstance(hipayConfig: any) {
        return new Promise((resolve, reject) => {
            scriptLoad(hipayConfig.sdk_url, true).then(() => {
                if (!this.hipayInstance) {
                    this.hipayInstance = new window.HiPay({
                        username: hipayConfig.username,
                        password: hipayConfig.password,
                        environment: hipayConfig.env,
                        lang: hipayConfig.lang,
                    });
                }
                if (!this.cardInstance) {
                    this.cardInstance = this.hipayInstance.create('card', {
                        template: 'auto',
                        selector: 'hipayhostedfields',
                    });
                }
                resolve({cardInstance: this.cardInstance, hipayInstance: this.hipayInstance});
            });
        });
    }
}
